import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Package, Lock, Unlock, Clock, Plus, Trash2, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDate } from '@/lib/utils'

export default function TimeCapsulePage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [openAt, setOpenAt] = useState('')
  const [mood, setMood] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [viewingCapsule, setViewingCapsule] = useState<any>(null)

  const { data: myCapsules } = useQuery({
    queryKey: ['my-capsules'],
    queryFn: () => api.get('/time-capsule/my').then(r => r.data),
    enabled: isAuthenticated
  })

  const { data: publicCapsules } = useQuery({
    queryKey: ['public-capsules'],
    queryFn: () => api.get('/time-capsule/public').then(r => r.data)
  })

  const createMutation = useMutation({
    mutationFn: () => api.post('/time-capsule', { title, content, openAt, moodAtCreation: mood, isPublic }),
    onSuccess: () => {
      setShowForm(false)
      setTitle('')
      setContent('')
      setOpenAt('')
      setMood('')
      setIsPublic(false)
      queryClient.invalidateQueries({ queryKey: ['my-capsules'] })
      toast({ title: t('timeCapsule.success.created') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('timeCapsule.error'), variant: 'destructive' })
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/time-capsule/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-capsules'] })
      toast({ title: t('timeCapsule.success.deleted') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const openMutation = useMutation({
    mutationFn: (id: string) => api.post(`/time-capsule/${id}/open`).then(r => r.data),
    onSuccess: (data) => {
      setViewingCapsule(data)
      queryClient.invalidateQueries({ queryKey: ['my-capsules'] })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('timeCapsule.error'), variant: 'destructive' })
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Package className="w-7 h-7 text-primary" />
        </div>
        <h1 className="font-display text-2xl font-bold">{t('timeCapsule.title')}</h1>
        <p className="text-muted-foreground mt-2">{t('timeCapsule.subtitle')}</p>
      </div>

      {isAuthenticated && (
        <button
          onClick={() => setShowForm(!showForm)}
          className="w-full mb-6 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5 text-primary" />
          <span className="font-medium">{t('timeCapsule.createButton')}</span>
        </button>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 p-5 rounded-2xl bg-card border border-border space-y-4">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('timeCapsule.form.titlePlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50" />
            <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t('timeCapsule.form.contentPlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none min-h-[120px]" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">{t('timeCapsule.form.openAtLabel')}</label>
                <input type="datetime-local" value={openAt} onChange={(e) => setOpenAt(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">{t('timeCapsule.form.moodLabel')}</label>
                <select value={mood} onChange={(e) => setMood(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm">
                  <option value="">{t('timeCapsule.form.selectPlaceholder')}</option>
                  {['happy', 'hopeful', 'calm', 'anxious', 'sad', 'angry', 'lonely', 'grateful'].map(m => (
                    <option key={m} value={m}>{t(`moodMap.mood.${m}`)}</option>
                  ))}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="rounded" />
              {t('timeCapsule.form.publicLabel')}
            </label>
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !title || !content || !openAt}>
              {createMutation.isPending ? t('timeCapsule.form.submitting') : t('timeCapsule.form.submit')}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {viewingCapsule && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-6 p-6 rounded-2xl bg-primary/10 border border-primary/30 relative">
          <button onClick={() => setViewingCapsule(null)} className="absolute top-3 right-3 p-1 rounded-lg hover:bg-primary/20">
            <Eye className="w-4 h-4" />
          </button>
          <div className="text-3xl mb-2">📬</div>
          <h3 className="font-semibold text-lg mb-2">{viewingCapsule.title}</h3>
          <p className="text-sm leading-relaxed whitespace-pre-wrap mb-3">{viewingCapsule.content}</p>
          <div className="text-xs text-muted-foreground">
            <p>{t('timeCapsule.view.createdAt')} {formatDate(viewingCapsule.createdAt)}</p>
            {viewingCapsule.moodAtCreation && <p>{t('timeCapsule.view.mood')} {t(`moodMap.mood.${viewingCapsule.moodAtCreation}`)}</p>}
          </div>
        </motion.div>
      )}

      {isAuthenticated && myCapsules?.length > 0 && (
        <div className="mb-8">
          <h2 className="font-semibold mb-4">{t('timeCapsule.myCapsules.title')}</h2>
          <div className="space-y-3">
            {myCapsules.map((capsule: any) => {
              const isOpenable = new Date() >= new Date(capsule.openAt)
              return (
                <div key={capsule._id} className="flex items-center justify-between p-4 rounded-xl bg-card border border-border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {capsule.isOpened ? <Unlock className="w-4 h-4 text-green-500" /> : <Lock className="w-4 h-4 text-muted-foreground" />}
                      <span className="font-medium">{capsule.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {capsule.isOpened ? `${t('timeCapsule.myCapsules.openedAt')} ${formatDate(capsule.openedAt)}` : `${t('timeCapsule.myCapsules.opensAt')} ${formatDate(capsule.openAt)}`}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {isOpenable && !capsule.isOpened && (
                      <button onClick={() => openMutation.mutate(capsule._id)} className="p-2 rounded-lg hover:bg-primary/10 text-primary">
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => deleteMutation.mutate(capsule._id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-semibold mb-4">{t('timeCapsule.community.title')}</h2>
        <div className="space-y-3">
          {publicCapsules?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">{t('timeCapsule.community.empty')}</div>
          )}
          {publicCapsules?.map((capsule: any) => (
            <div key={capsule._id} className="p-4 rounded-xl bg-card border border-border">
              <h3 className="font-medium mb-1">{capsule.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{capsule.content}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>{t('timeCapsule.communityWrittenOpened', { createdAt: formatDate(capsule.createdAt), openAt: formatDate(capsule.openAt) })}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
